// Driver authentication utility functions

/**
 * Get driver token from multiple sources (Redux state, localStorage)
 * @param {Object} driverAuthState - The driverAuth state from Redux
 * @returns {string|null} - The driver token or null if not found
 */
export const getDriverToken = (driverAuthState) => {
  // Try multiple sources for the token - check localStorage first for persistence
  const localStorageToken = localStorage.getItem('driverToken');
  const reduxToken = driverAuthState?.token;
  const driverToken = driverAuthState?.driver?.token;

  const token = localStorageToken || reduxToken || driverToken;


  return token;
};

/**
 * Get driver data from multiple sources (Redux state, localStorage)
 * @param {Object} driverAuthState - The driverAuth state from Redux
 * @returns {Object|null} - The driver data or null if not found
 */
export const getDriverData = (driverAuthState) => {
  let driver = driverAuthState?.driver;
  
  // If no driver in Redux, try localStorage
  if (!driver) {
    try {
      const storedDriver = localStorage.getItem('driver');
      if (storedDriver) {
        driver = JSON.parse(storedDriver);
      }
    } catch (error) {
      console.error('Error parsing driver from localStorage:', error);
    }
  }
  
  
  return driver;
};

/**
 * Check if driver is properly authenticated
 * @param {Object} driverAuthState - The driverAuth state from Redux
 * @returns {boolean} - True if driver is authenticated
 */
export const isDriverAuthenticated = (driverAuthState) => {
  const token = getDriverToken(driverAuthState);
  const driver = getDriverData(driverAuthState);

  // Basic validation - be more lenient to avoid clearing valid sessions
  const hasValidToken = !!(token && token.length > 10); // Basic token length check
  const hasValidDriver = !!(driver && (driver._id || driver.id));

  // More lenient token validation - only check if token exists and is not obviously expired
  const tokenIsValid = !token || !isTokenExpired(token);

  const isAuthenticated = hasValidToken && hasValidDriver && tokenIsValid;


  // Only clear data if token is definitely expired AND malformed
  // Be very conservative about clearing session data
  if (token && isTokenExpired(token) && token.length < 10) {
    clearCorruptedAuthData();
    return false;
  }

  return isAuthenticated;
};

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} - True if token is expired
 */
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    // Parse JWT token payload (basic validation)
    const parts = token.split('.');
    if (parts.length !== 3) return false; // Invalid JWT format, but don't consider expired

    const base64Url = parts[1];
    if (!base64Url) return false;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));

    // Check if token has exp field and if it's expired
    if (payload.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      const isExpired = payload.exp < currentTime;


      return isExpired;
    }

    // If no exp field, consider token valid (fallback)
    return false;
  } catch (error) {
    return false; // If we can't parse it, don't consider it expired to avoid clearing sessions
  }
};

/**
 * Check if driver object has all required fields
 * @param {Object} driver - Driver object
 * @returns {boolean} - True if driver has required fields
 */
const hasRequiredDriverFields = (driver) => {
  if (!driver) return false;

  // Only require the most critical fields to avoid unnecessary session clearing
  const requiredFields = ['_id'];
  return requiredFields.every(field => driver[field] && driver[field].toString().trim().length > 0);
};

/**
 * Clear corrupted authentication data from localStorage
 * Only call this when absolutely necessary
 */
const clearCorruptedAuthData = () => {

  localStorage.removeItem('driver');
  localStorage.removeItem('driverToken');

};

/**
 * Get authentication headers for API calls
 * @param {Object} driverAuthState - The driverAuth state from Redux
 * @returns {Object} - Headers object with Authorization
 */
export const getDriverAuthHeaders = (driverAuthState) => {
  const token = getDriverToken(driverAuthState);
  
  if (!token) {
    throw new Error('No driver authentication token available');
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};
